import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowUpRight, User } from "lucide-react";
import HorizontalAccordion from "@/components/HorizontalAccordion";

const SobreNos = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Sobre a AmeUp — Automação para Buffets"
        description="Conheça a AmeUp: eliminamos o operacional do dia a dia de buffets e empresas de eventos. Veja nossa missão e time."
        canonical="https://ameup.com.br/sobre-nos"
        ogType="website"
        ogTitle="Sobre a AmeUp — Automação para Buffets"
        ogDescription="Conheça a AmeUp: eliminamos o operacional do dia a dia de buffets e empresas de eventos. Veja nossa missão e time."
      />
      <Navbar />

      <main className="flex-grow">
        {/* SEÇÃO 1 — Hero */}
        <section className="py-20 md:py-32 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#eefda3]/30 via-transparent to-transparent pointer-events-none" />
          <div className="container-custom max-w-3xl relative z-10">
            <Badge variant="secondary" className="mb-6 text-xs tracking-widest font-semibold uppercase bg-[#eefda3] text-brand-dark hover:bg-[#eefda3]/90 border-transparent">
              Nossa História
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-foreground leading-tight">
              Viemos do setor financeiro.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Ao investigarmos com profundidade diversos setores, descobrimos o mercado de eventos. Um ecossistema que movimenta bilhões por ano no Brasil, mas que ainda carecia de processos e tecnologia para prosperar sem sacrificar a vida pessoal do dono do negócio.
            </p>
          </div>
        </section>

        {/* SEÇÃO 2 — A Origem (Storytelling) */}
        <section className="py-16 md:py-24">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="inline-block">
                  <div className="w-12 h-1 bg-[#eefda3] rounded-full mb-6" />
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                    O problema que a gente viveu na pele
                  </h2>
                </div>
                <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
                  Vimos de perto a realidade de quem trabalha com eventos no Brasil: um negócio
                  que depende inteiramente do dono. Cada proposta respondido no WhatsApp, cada
                  contrato gerado no Word, cada cobrança feita manualmente, horas desperdiçadas
                  em tarefas que simplesmente não deveriam existir.
                </p>
                <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
                  O setor de eventos movimenta bilhões por ano no Brasil, mas opera como se
                  fosse 2005. Faltava tecnologia feita para esse mercado, não adaptada dele.
                </p>
                <p className="inline-block text-xl md:text-2xl font-bold text-brand-dark bg-[#eefda3] px-3 py-1 rounded-md">
                  Foi aí que surgiu a AmeUp.
                </p>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-[#eefda3]/40">
                <img
                  src="https://qfozcnxbtjjmidhjdepc.supabase.co/storage/v1/object/public/sobrenos/representation-user-experience-interface-design.jpg"
                  alt="Equipe trabalhando na digitalização de processos"
                  className="w-full h-[380px] md:h-[500px] object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* SEÇÃO 3 — Missão */}
        <section className="py-16 md:py-20 bg-muted/50">
          <div className="container-custom max-w-3xl text-center">
            <Badge variant="secondary" className="mb-6 text-xs tracking-widest font-semibold uppercase bg-[#eefda3] text-brand-dark hover:bg-[#eefda3]/90 border-transparent">
              Buscamos
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
              Devolver tempo para quem faz eventos acontecerem.
            </h2>
            <p className="text-muted-foreground leading-relaxed text-lg">
              Nosso objetivo é eliminar cada etapa manual e repetitiva da operação de um
              buffet — do primeiro contato com o cliente até o pagamento recebido. Não porque
              queremos substituir o toque humano de um evento bem feito, mas porque acreditamos
              que o talento de quem organiza festas merece ser gasto no que realmente importa:
              o cliente, o evento, o crescimento.
            </p>
          </div>
        </section>

        {/* SEÇÃO 4 — Valores */}
        <section id="valores" className="py-10 md:py-14">
          <div className="container-custom">
            <div className="text-center mb-4">
              <Badge variant="secondary" className="mb-2 text-xs tracking-widest font-semibold uppercase bg-[#eefda3] text-brand-dark hover:bg-[#eefda3]/90 border-transparent">
                O que nos move
              </Badge>
            </div>
            
            <HorizontalAccordion />
          </div>
        </section>


        {/* SEÇÃO 6 — CTA Final */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#eefda3] to-[#c8e87a]">
          <div className="container-custom text-center max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-brand-dark">
              Seu buffet pode operar diferente. A partir de hoje.
            </h2>
            <p className="text-brand-dark/70 text-lg mb-8">
              Junte-se aos fornecedores que já trocaram o operacional pelo crescimento.
            </p>
            <div className="flex justify-center">
              <Button asChild size="lg" className="bg-brand-dark text-white hover:bg-brand-dark/90 font-semibold text-base px-8">
                <Link to="/cadastro-fornecedor">Começar grátis agora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer hidePaymentMethods={true} />
    </div>
  );
};

export default SobreNos;
